import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { AuthService } from '@core/auth/services/auth.service';
import { HttpRequestService } from '@core/http/http-request.service';

interface ApiResponse<T> {
  data: T;
  message: string;
}

interface CreditCheckResponse {
  customer_id: number;
  document_type_id: number;
  document_type_alias: string;
  document_number: string;
  masked_email?: string;
  masked_phone?: string;
  has_active_credit: boolean;
  credits: BackendCredit[];
  personal_accounts?: BackendPersonalAccount[];
}

interface OtpRequestResponse {
  customer_id: number;
  document_number: string;
  delivery_channels: string[];
  masked_email?: string;
  masked_phone?: string;
  expires_at: string;
}

interface OtpVerifyResponse {
  token: string;
  customer_id: number;
  document_number: string;
}

interface BackendCredit {
  id: number;
  credit_number: string;
  name: string;
  payroll_entity: string;
  source_credit_hash?: string | null;
  disbursement_account?: BackendDisbursementAccount | null;
  balance: number;
  available_amount: number | null;
  next_payment_date: string | null;
  next_payment_amount: number | null;
  status: string;
  disbursement_date?: string | null;
  deduction_start_date?: string | null;
  is_eligible?: boolean;
  is_operable?: boolean;
  operable_reason?: string | null;
  has_nightly_result?: boolean;
  disbursement_block_reason?: string | null;
  has_active_rollover?: boolean;
  active_rollover_request?: BackendRolloverRequest | null;
  latest_rollover_request?: BackendRolloverRequest | null;
  rollover_summary?: BackendRolloverSummary | null;
  opened_at?: string | null;
  closed_at?: string | null;
  history?: CreditTransaction[];
  transactions?: CreditTransaction[];
}

interface BackendSimulation {
  credit_id: number;
  credit_number: string;
  requested_amount: number;
  available_amount: number;
  term_months: number;
  interest_rate: number;
  monthly_payment: number;
  simulation_id?: string | null;
  estimated_disbursement_date: string;
  first_payment_date: string;
  amortization?: BackendAmortizationRow[];
}

interface BackendPersonalAccount {
  id: number;
  label: string;
  name: string;
  document: string;
  banking_entity_id: number | null;
  banking_entity_name: string | null;
  account_type: string | null;
  account_number: string | null;
  masked_account_number: string | null;
}

interface BackendDisbursementAccount {
  source?: string | null;
  turn_id?: number | null;
  name?: string | null;
  document?: string | null;
  banking_entity_id?: number | null;
  banking_entity_name?: string | null;
  account_type?: string | null;
  account_number?: string | null;
  masked_account_number?: string | null;
  disbursement_date?: string | null;
}

interface BackendRolloverRequest {
  rollover_credit_id?: number | null;
  rollover_hash?: string | null;
  status?: string | null;
  status_label?: string | null;
  requested_disbursement?: number | null;
  new_rollover_balance?: number | null;
  total_discounts?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
}

interface BackendRolloverSummary {
  counts?: {
    active?: number;
    approved?: number;
    denied?: number;
  } | null;
  totals?: {
    active?: number;
    approved?: number;
    denied?: number;
  } | null;
}

interface BackendCompletedDisbursementSummary {
  disbursement_id: number;
  process_id: string;
  credit_id: number | string | null;
  product_name: string;
  requested_amount: number;
  transaction_date: string;
  destination_account?: BackendPersonalAccount | null;
}

interface BackendAmortizationRow {
  period: number;
  payment_date: string;
  installment: number;
  interest: number;
  capital: number;
  increment: number;
  balance: number;
}

export interface CustomerAuth {
  customerId: number;
  document_type_id: string;
  document_type_alias?: string;
  document: string;
  maskedEmail?: string;
  maskedPhone?: string;
  hasActiveCredit: boolean;
  otpExpiration?: Date;
  personalAccounts: PersonalAccount[];
}

export interface CreditTransaction {
  id: string;
  date: Date;
  type: 'disbursement' | 'payment' | 'rollover';
  amount: number;
  signedAmount?: number;
  direction?: 'in' | 'out';
  description: string;
  status?: 'active' | 'approved' | 'denied' | null;
  statusLabel?: string | null;
}

export interface Credit {
  id: string;
  creditNumber: string;
  name: string;
  pagaduria: string;
  sourceCreditHash?: string | null;
  disbursementAccount?: PersonalAccount | null;
  balance: number;
  availableAmount: number | null;
  nextPaymentDate: Date | null;
  nextPaymentAmount: number | null;
  disbursementDate?: Date | null;
  deductionStartDate?: Date | null;
  isEligible: boolean;
  isOperable: boolean;
  operableReason?: string | null;
  hasNightlyResult: boolean;
  disbursementBlockReason?: string | null;
  hasActiveRollover: boolean;
  activeRolloverRequest?: RolloverRequest | null;
  latestRolloverRequest?: RolloverRequest | null;
  rolloverSummary?: RolloverSummary | null;
  status: 'Al dia' | 'En mora' | 'Finalizado';
  history: CreditTransaction[];
  transactions: CreditTransaction[];
}

export interface DisbursementSimulation {
  creditId: string;
  creditNumber: string;
  requestedAmount: number;
  availableAmount: number;
  termMonths: number;
  interestRate: number;
  monthlyPayment: number;
  simulationId?: string | null;
  estimatedDisbursementDate: Date;
  firstPaymentDate: Date;
  amortization: DisbursementAmortizationRow[];
}

export interface DisbursementAmortizationRow {
  period: number;
  paymentDate: Date;
  installment: number;
  interest: number;
  capital: number;
  increment: number;
  balance: number;
}

export interface PersonalAccount {
  id: number;
  label: string;
  name: string;
  document: string;
  bankingEntityId: number | null;
  bankingEntityName: string | null;
  accountType: string | null;
  accountNumber: string | null;
  maskedAccountNumber: string | null;
}

export interface RolloverRequest {
  rolloverCreditId: number | null;
  rolloverHash: string | null;
  status: 'active' | 'approved' | 'denied' | null;
  statusLabel: string | null;
  requestedDisbursement: number;
  newRolloverBalance: number;
  totalDiscounts: number;
  createdAt: Date | null;
  updatedAt: Date | null;
}

export interface RolloverSummary {
  counts: {
    active: number;
    approved: number;
    denied: number;
  };
  totals: {
    active: number;
    approved: number;
    denied: number;
  };
}

export interface CompletedDisbursementSummary {
  disbursementId: number;
  processId: string;
  creditId: string | null;
  productName: string;
  requestedAmount: number;
  transactionDate: Date;
  destinationAccount: PersonalAccount | null;
}

export interface CreatedDisbursement {
  disbursementId: number;
  status: string;
  documentsCount: number;
  simulation: DisbursementSimulation;
  destinationAccount: PersonalAccount | null;
}

@Injectable({
  providedIn: 'root'
})
export class PersonasAuthService {
  private readonly http = inject(HttpRequestService);
  private readonly authService = inject(AuthService);

  private readonly currentUserStorageKey = 'persons_current_user';
  private readonly creditsStorageKey = 'persons_credits';
  private readonly activeCreditStorageKey = 'persons_active_credit_id';
  private readonly disbursementStorageKey = 'persons_current_disbursement_id';
  private readonly simulationStorageKey = 'persons_current_simulation';
  private readonly completedDisbursementStorageKey = 'persons_completed_disbursement';

  private currentUserSubject = new BehaviorSubject<CustomerAuth | null>(this.readCurrentUser());
  public currentUser$ = this.currentUserSubject.asObservable();

  private creditsSubject = new BehaviorSubject<Credit[]>(this.readCredits());
  public credits$ = this.creditsSubject.asObservable();

  private activeCreditSubject = new BehaviorSubject<Credit | null>(this.readActiveCredit());
  public activeCredit$ = this.activeCreditSubject.asObservable();

  constructor() {
    this.syncActiveCreditFromStorage();
  }

  checkActiveCredit(document_type_id: string, document: string): Observable<CustomerAuth> {
    return this.http.post<ApiResponse<CreditCheckResponse>>(
      '/customers/auth/credit-check',
      {
        document_type_id,
        document_number: document
      },
      true
    ).pipe(
      map((response) => response.data),
      tap((data) => {
        const credits = data.credits.map((credit) => this.mapCredit(credit));
        const currentUser: CustomerAuth = {
          customerId: data.customer_id,
          document_type_id: String(data.document_type_id),
          document_type_alias: data.document_type_alias,
          document: data.document_number,
          maskedEmail: data.masked_email,
          maskedPhone: data.masked_phone,
          hasActiveCredit: data.has_active_credit,
          personalAccounts: (data.personal_accounts ?? [])
            .map((account) => this.mapPersonalAccount(account))
            .filter((account): account is PersonalAccount => account !== null)
        };
        this.setCurrentUser(currentUser);
        this.setCredits(credits);
      }),
      map(() => this.getCurrentUser() as CustomerAuth),
      catchError((error) => this.handleApiError(error, 'No se encontro un credito activo con estos datos.'))
    );
  }

  requestOtp(): Observable<boolean> {
    const currentUser = this.getCurrentUser();
    if (!currentUser) {
      return throwError(() => new Error('No encontramos una sesion activa para solicitar el codigo.'));
    }

    return this.http.post<ApiResponse<OtpRequestResponse>>(
      '/customers/otp/request',
      {
        document_type_id: currentUser.document_type_id,
        document_number: currentUser.document
      },
      true
    ).pipe(
      tap((response) => {
        this.setCurrentUser({
          ...currentUser,
          maskedEmail: response.data.masked_email,
          maskedPhone: response.data.masked_phone,
          otpExpiration: new Date(response.data.expires_at)
        });
      }),
      map(() => true),
      catchError((error) => this.handleApiError(error, 'No pudimos enviar el codigo OTP.'))
    );
  }

  verifyOtp(otpCode: string): Observable<boolean> {
    const currentUser = this.getCurrentUser();
    if (!currentUser) {
      return throwError(() => new Error('No encontramos una sesion activa para validar el codigo.'));
    }

    return this.http.post<ApiResponse<OtpVerifyResponse>>(
      '/customers/otp/verify',
      {
        document_type_id: currentUser.document_type_id,
        document_number: currentUser.document,
        code: otpCode
      },
      true
    ).pipe(
      tap((response) => this.authService.saveCustomerToken(response.data.token)),
      map(() => true),
      catchError((error) => this.handleApiError(error, 'El codigo ingresado no es valido o ha expirado.'))
    );
  }

  loadCredits(): Observable<Credit[]> {
    return this.http.get<ApiResponse<BackendCredit[]>>('/customers/credits').pipe(
      map((response) => response.data.map((credit) => this.mapCredit(credit))),
      tap((credits) => this.setCredits(credits)),
      catchError((error) => this.handleApiError(error, 'No pudimos cargar los creditos disponibles.'))
    );
  }

  loadCreditDetail(creditId: string): Observable<Credit> {
    return this.http.get<ApiResponse<BackendCredit>>(`/customers/credits/${creditId}`).pipe(
      map((response) => this.mapCredit(response.data)),
      tap((credit) => this.upsertCredit(credit, true)),
      catchError((error) => this.handleApiError(error, 'No pudimos cargar el detalle del credito.'))
    );
  }

  loadTransactions(creditId: string): Observable<CreditTransaction[]> {
    return this.http.get<ApiResponse<any[]>>(`/customers/credits/${creditId}/transactions`).pipe(
      map((response) => response.data.map((item) => this.mapTransaction(item))),
      catchError((error) => this.handleApiError(error, 'No pudimos cargar las transacciones del credito.'))
    );
  }

  simulateDisbursement(amount: string | number, termMonths = 24): Observable<DisbursementSimulation> {
    const activeCredit = this.getActiveCreditValue();
    if (!activeCredit) {
      return throwError(() => new Error('No hay un credito seleccionado para simular el desembolso.'));
    }

    return this.http.post<ApiResponse<BackendSimulation>>('/customers/disbursements/simulate', {
      credit_id: activeCredit.id,
      amount: this.normalizeAmount(amount),
      term_months: termMonths
    }).pipe(
      map((response) => this.mapSimulation(response.data)),
      tap((simulation) => this.setCurrentSimulation(simulation)),
      catchError((error) => this.handleApiError(error, 'No pudimos simular el desembolso.'))
    );
  }

  createDisbursement(
    amount: string | number,
    destinationAccountId: number,
    termMonths = 24
  ): Observable<CreatedDisbursement> {
    const activeCredit = this.getActiveCreditValue();
    if (!activeCredit) {
      return throwError(() => new Error('No hay un credito seleccionado para crear el desembolso.'));
    }

    const currentSimulation = this.getCurrentSimulation();
    return this.http.post<ApiResponse<any>>('/customers/disbursements', {
      credit_id: activeCredit.id,
      amount: this.normalizeAmount(amount),
      term_months: termMonths,
      simulation_id: currentSimulation?.simulationId ?? undefined,
      destination_account_id: destinationAccountId
    }).pipe(
      map((response) => ({
        disbursementId: response.data.disbursement_id,
        status: response.data.status,
        documentsCount: response.data.documents_count,
        simulation: this.mapSimulation(response.data.simulation),
        destinationAccount: this.mapPersonalAccount(response.data.destination_account)
      })),
      tap((created) => {
        this.clearCompletedDisbursementSummary();
        this.setCurrentDisbursementId(created.disbursementId);
      }),
      catchError((error) => this.handleApiError(error, 'No pudimos crear la solicitud de desembolso.'))
    );
  }

  saveCurrentRolloverProcess(): Observable<{ processId: string }> {
    const disbursementId = this.getCurrentDisbursementId();
    if (!disbursementId) {
      return throwError(() => new Error('No hay una solicitud de desembolso activa para guardar.'));
    }

    return this.http.post<ApiResponse<{ process_id: string }>>(
      `/customers/disbursements/${disbursementId}/rollover/save`,
      {}
    ).pipe(
      map((response) => ({
        processId: response.data.process_id
      })),
      catchError((error) => this.handleApiError(error, 'No pudimos guardar el proceso de rollover.'))
    );
  }

  createCurrentRolloverCredit(): Observable<CompletedDisbursementSummary> {
    const disbursementId = this.getCurrentDisbursementId();
    if (!disbursementId) {
      return throwError(() => new Error('No hay una solicitud de desembolso activa para finalizar.'));
    }

    return this.http.post<ApiResponse<unknown>>(
      `/customers/disbursements/${disbursementId}/rollover/create`,
      {}
    ).pipe(
      map((response: any) => this.mapCompletedDisbursementSummary(response.data.summary)),
      tap((summary) => this.setCompletedDisbursementSummary(summary)),
      catchError((error) => this.handleApiError(error, 'No pudimos completar el rollover del credito.'))
    );
  }

  setActiveCredit(creditId: string) {
    const credit = this.creditsSubject.value.find((item) => item.id === creditId) ?? null;
    this.activeCreditSubject.next(credit);
    if (credit) {
      localStorage.setItem(this.activeCreditStorageKey, credit.id);
    } else {
      localStorage.removeItem(this.activeCreditStorageKey);
    }
  }

  getActiveCreditValue(): Credit | null {
    return this.activeCreditSubject.value;
  }

  getCreditsValue(): Credit[] {
    return this.creditsSubject.value;
  }

  getCurrentUser(): CustomerAuth | null {
    return this.currentUserSubject.value;
  }

  getCurrentDisbursementId(): number | null {
    const raw = localStorage.getItem(this.disbursementStorageKey);
    return raw ? Number(raw) : null;
  }

  setCurrentDisbursementId(disbursementId: number) {
    localStorage.setItem(this.disbursementStorageKey, String(disbursementId));
  }

  clearCurrentDisbursement() {
    localStorage.removeItem(this.disbursementStorageKey);
  }

  getCompletedDisbursementSummary(): CompletedDisbursementSummary | null {
    const raw = localStorage.getItem(this.completedDisbursementStorageKey);
    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw);
      return {
        ...parsed,
        creditId: parsed.creditId ?? null,
        transactionDate: new Date(parsed.transactionDate)
      };
    } catch {
      return null;
    }
  }

  setCompletedDisbursementSummary(summary: CompletedDisbursementSummary) {
    localStorage.setItem(this.completedDisbursementStorageKey, JSON.stringify({
      ...summary,
      transactionDate: summary.transactionDate.toISOString()
    }));
  }

  clearCompletedDisbursementSummary() {
    localStorage.removeItem(this.completedDisbursementStorageKey);
  }

  getCurrentSimulation(): DisbursementSimulation | null {
    const raw = localStorage.getItem(this.simulationStorageKey);
    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw);
      return this.deserializeSimulation(parsed);
    } catch {
      return null;
    }
  }

  setCurrentSimulation(simulation: DisbursementSimulation) {
    localStorage.setItem(this.simulationStorageKey, JSON.stringify(this.serializeSimulation(simulation)));
  }

  clearCurrentSimulation() {
    localStorage.removeItem(this.simulationStorageKey);
  }

  logout() {
    localStorage.removeItem(this.currentUserStorageKey);
    localStorage.removeItem(this.creditsStorageKey);
    localStorage.removeItem(this.activeCreditStorageKey);
    localStorage.removeItem(this.disbursementStorageKey);
    localStorage.removeItem(this.simulationStorageKey);
    localStorage.removeItem(this.completedDisbursementStorageKey);
    this.currentUserSubject.next(null);
    this.creditsSubject.next([]);
    this.activeCreditSubject.next(null);
    this.authService.logout();
  }

  private setCurrentUser(user: CustomerAuth) {
    localStorage.setItem(this.currentUserStorageKey, JSON.stringify({
      ...user,
      otpExpiration: user.otpExpiration?.toISOString() ?? null
    }));
    this.currentUserSubject.next(user);
  }

  private setCredits(credits: Credit[]) {
    localStorage.setItem(this.creditsStorageKey, JSON.stringify(credits.map((credit) => ({
      ...credit,
      nextPaymentDate: credit.nextPaymentDate?.toISOString() ?? null,
      disbursementDate: credit.disbursementDate?.toISOString() ?? null,
      deductionStartDate: credit.deductionStartDate?.toISOString() ?? null,
      history: credit.history.map((row) => ({ ...row, date: row.date.toISOString() })),
      transactions: credit.transactions.map((row) => ({ ...row, date: row.date.toISOString() }))
    }))));
    this.creditsSubject.next(credits);

    const currentActiveId = localStorage.getItem(this.activeCreditStorageKey);
    const nextActive = credits.find((credit) => credit.id === currentActiveId) ?? credits[0] ?? null;
    this.activeCreditSubject.next(nextActive);
    if (nextActive) {
      localStorage.setItem(this.activeCreditStorageKey, nextActive.id);
    }
  }

  private upsertCredit(credit: Credit, setAsActive = false) {
    const currentCredits = this.creditsSubject.value;
    const updatedCredits = currentCredits.some((item) => item.id === credit.id)
      ? currentCredits.map((item) => item.id === credit.id ? credit : item)
      : [...currentCredits, credit];

    this.setCredits(updatedCredits);
    if (setAsActive) {
      this.setActiveCredit(credit.id);
    }
  }

  private readCurrentUser(): CustomerAuth | null {
    const raw = localStorage.getItem(this.currentUserStorageKey);
    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw);
      return {
        ...parsed,
        otpExpiration: parsed.otpExpiration ? new Date(parsed.otpExpiration) : undefined,
        personalAccounts: (parsed.personalAccounts ?? []).filter((account: any) => account != null)
      };
    } catch {
      return null;
    }
  }

  private readCredits(): Credit[] {
    const raw = localStorage.getItem(this.creditsStorageKey);
    if (!raw) {
      return [];
    }

    try {
      return JSON.parse(raw).map((item: any) => ({
        ...item,
        availableAmount: item.availableAmount == null ? null : Number(item.availableAmount),
        nextPaymentDate: item.nextPaymentDate ? new Date(item.nextPaymentDate) : null,
        disbursementDate: item.disbursementDate ? new Date(item.disbursementDate) : null,
        deductionStartDate: item.deductionStartDate ? new Date(item.deductionStartDate) : null,
        history: (item.history ?? []).map((row: any) => ({
          ...row,
          date: new Date(row.date)
        })),
        transactions: (item.transactions ?? []).map((row: any) => ({
          ...row,
          date: new Date(row.date)
        }))
      }));
    } catch {
      return [];
    }
  }

  private readActiveCredit(): Credit | null {
    const activeId = localStorage.getItem(this.activeCreditStorageKey);
    if (!activeId) {
      return this.readCredits()[0] ?? null;
    }
    return this.readCredits().find((credit) => credit.id === activeId) ?? null;
  }

  private syncActiveCreditFromStorage() {
    const credits = this.creditsSubject.value;
    if (credits.length === 0) {
      return;
    }

    const activeId = localStorage.getItem(this.activeCreditStorageKey);
    const activeCredit = credits.find((credit) => credit.id === activeId) ?? credits[0];
    this.activeCreditSubject.next(activeCredit);
  }

  private mapCredit(credit: BackendCredit): Credit {
    return {
      id: String(credit.id),
      creditNumber: credit.credit_number,
      name: credit.name,
      pagaduria: credit.payroll_entity,
      sourceCreditHash: credit.source_credit_hash ?? null,
      disbursementAccount: this.mapDisbursementAccount(credit.disbursement_account),
      balance: Number(credit.balance),
      availableAmount: credit.available_amount == null ? null : Number(credit.available_amount),
      nextPaymentDate: credit.next_payment_date ? new Date(credit.next_payment_date) : null,
      nextPaymentAmount: credit.next_payment_amount == null ? null : Number(credit.next_payment_amount),
      disbursementDate: credit.disbursement_date ? new Date(credit.disbursement_date) : null,
      deductionStartDate: credit.deduction_start_date ? new Date(credit.deduction_start_date) : null,
      isEligible: Boolean(credit.is_eligible),
      isOperable: credit.is_operable !== false,
      operableReason: credit.operable_reason ?? null,
      hasNightlyResult: Boolean(credit.has_nightly_result),
      disbursementBlockReason: credit.disbursement_block_reason ?? null,
      hasActiveRollover: Boolean(credit.has_active_rollover),
      activeRolloverRequest: this.mapRolloverRequest(credit.active_rollover_request),
      latestRolloverRequest: this.mapRolloverRequest(credit.latest_rollover_request),
      rolloverSummary: this.mapRolloverSummary(credit.rollover_summary),
      status: this.mapStatus(credit.status),
      history: (credit.history ?? []).map((item) => this.mapTransaction(item)),
      transactions: (credit.transactions ?? []).map((item) => this.mapTransaction(item))
    };
  }

  private mapPersonalAccount(account: BackendPersonalAccount | null | undefined): PersonalAccount | null {
    if (!account) {
      return null;
    }

    return {
      id: Number(account.id),
      label: account.label,
      name: account.name,
      document: account.document,
      bankingEntityId: account.banking_entity_id == null ? null : Number(account.banking_entity_id),
      bankingEntityName: account.banking_entity_name ?? null,
      accountType: account.account_type ?? null,
      accountNumber: account.account_number ?? null,
      maskedAccountNumber: account.masked_account_number ?? null
    };
  }

  private mapTransaction(transaction: any): CreditTransaction {
    const direction = transaction.direction === 'in' ? 'in' : 'out';
    const signedAmount = transaction.signed_amount == null
      ? Number(transaction.amount)
      : Number(transaction.signed_amount);
    const type = transaction.type === 'rollover'
      ? 'rollover'
      : transaction.type === 'payment' || direction === 'in'
        ? 'payment'
        : 'disbursement';

    return {
      id: String(transaction.id),
      date: new Date(transaction.date),
      type,
      amount: Number(transaction.amount),
      signedAmount,
      direction,
      description: transaction.description,
      status: transaction.status ?? null,
      statusLabel: transaction.status_label ?? null
    };
  }

  private mapDisbursementAccount(
    account: BackendDisbursementAccount | null | undefined
  ): PersonalAccount | null {
    if (!account) {
      return null;
    }

    return {
      id: Number(account.turn_id ?? account.banking_entity_id ?? 0),
      label: [
        account.banking_entity_name,
        account.account_type,
        account.masked_account_number ?? account.account_number
      ].filter(Boolean).join(' · '),
      name: account.name ?? '',
      document: account.document ?? '',
      bankingEntityId: account.banking_entity_id == null ? null : Number(account.banking_entity_id),
      bankingEntityName: account.banking_entity_name ?? null,
      accountType: account.account_type ?? null,
      accountNumber: account.account_number ?? null,
      maskedAccountNumber: account.masked_account_number ?? null
    };
  }

  private mapRolloverRequest(request: BackendRolloverRequest | null | undefined): RolloverRequest | null {
    if (!request) {
      return null;
    }

    const normalizedStatus = request.status == null
      ? null
      : String(request.status).toLowerCase() as 'active' | 'approved' | 'denied';

    return {
      rolloverCreditId: request.rollover_credit_id == null ? null : Number(request.rollover_credit_id),
      rolloverHash: request.rollover_hash ?? null,
      status: normalizedStatus,
      statusLabel: request.status_label ?? null,
      requestedDisbursement: Number(request.requested_disbursement ?? 0),
      newRolloverBalance: Number(request.new_rollover_balance ?? 0),
      totalDiscounts: Number(request.total_discounts ?? 0),
      createdAt: request.created_at ? new Date(request.created_at) : null,
      updatedAt: request.updated_at ? new Date(request.updated_at) : null
    };
  }

  private mapRolloverSummary(summary: BackendRolloverSummary | null | undefined): RolloverSummary | null {
    if (!summary) {
      return null;
    }

    return {
      counts: {
        active: Number(summary.counts?.active ?? 0),
        approved: Number(summary.counts?.approved ?? 0),
        denied: Number(summary.counts?.denied ?? 0)
      },
      totals: {
        active: Number(summary.totals?.active ?? 0),
        approved: Number(summary.totals?.approved ?? 0),
        denied: Number(summary.totals?.denied ?? 0)
      }
    };
  }

  private mapSimulation(simulation: BackendSimulation): DisbursementSimulation {
    return {
      creditId: String(simulation.credit_id),
      creditNumber: simulation.credit_number,
      requestedAmount: Number(simulation.requested_amount),
      availableAmount: Number(simulation.available_amount),
      termMonths: Number(simulation.term_months),
      interestRate: Number(simulation.interest_rate),
      monthlyPayment: Number(simulation.monthly_payment),
      simulationId: simulation.simulation_id ?? null,
      estimatedDisbursementDate: new Date(simulation.estimated_disbursement_date),
      firstPaymentDate: new Date(simulation.first_payment_date),
      amortization: (simulation.amortization ?? []).map((row) => ({
        period: Number(row.period),
        paymentDate: new Date(row.payment_date),
        installment: Number(row.installment),
        interest: Number(row.interest),
        capital: Number(row.capital),
        increment: Number(row.increment),
        balance: Number(row.balance)
      }))
    };
  }

  private mapCompletedDisbursementSummary(
    summary: BackendCompletedDisbursementSummary | null | undefined
  ): CompletedDisbursementSummary {
    return {
      disbursementId: Number(summary?.disbursement_id ?? 0),
      processId: String(summary?.process_id ?? ''),
      creditId: summary?.credit_id == null ? null : String(summary.credit_id),
      productName: summary?.product_name ?? 'Libranza Dinamica',
      requestedAmount: Number(summary?.requested_amount ?? 0),
      transactionDate: new Date(summary?.transaction_date ?? new Date().toISOString()),
      destinationAccount: this.mapPersonalAccount(summary?.destination_account) ?? null
    };
  }

  private serializeSimulation(simulation: DisbursementSimulation) {
    return {
      ...simulation,
      estimatedDisbursementDate: simulation.estimatedDisbursementDate.toISOString(),
      firstPaymentDate: simulation.firstPaymentDate.toISOString(),
      amortization: simulation.amortization.map((row) => ({
        ...row,
        paymentDate: row.paymentDate.toISOString()
      }))
    };
  }

  private deserializeSimulation(parsed: any): DisbursementSimulation {
    return {
      ...parsed,
      simulationId: parsed.simulationId ?? null,
      estimatedDisbursementDate: new Date(parsed.estimatedDisbursementDate),
      firstPaymentDate: new Date(parsed.firstPaymentDate),
      amortization: (parsed.amortization ?? []).map((row: any) => ({
        ...row,
        paymentDate: new Date(row.paymentDate)
      }))
    };
  }

  private mapStatus(status: string): 'Al dia' | 'En mora' | 'Finalizado' {
    const normalized = status.toLowerCase();
    if (normalized.includes('mora')) {
      return 'En mora';
    }
    if (normalized.includes('final')) {
      return 'Finalizado';
    }
    return 'Al dia';
  }

  private normalizeAmount(amount: string | number): string {
    return String(amount).replace(/[^\d]/g, '');
  }

  private handleApiError(error: any, fallbackMessage: string) {
    const message = error?.error?.message || fallbackMessage;
    return throwError(() => new Error(message));
  }
}
